import { ButtonIcon, Dialog, useDialog } from "@fpsoftware/mvp";
import { zodResolver } from "@hookform/resolvers/zod";
import { LocalDate } from "@js-joda/core";
import BigNumber from "bignumber.js";
import { isEqual } from "lodash-es";
import { useCallback, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { TbDeviceFloppy, TbPlus, TbTrash } from "react-icons/tb";
import {
  type Address,
  formatAmount,
  formatMoney,
  getPrepaidTaxMonthsDue,
  getTaxOptions,
  type Mortgage,
  type MortgageTax,
  type PropertyTaxJson,
  type PropertyTaxPayment,
  PropertyTaxPaymentSchema,
  propertyTaxToJson,
  type State,
  StateNames,
  type TaxOptions,
} from "shared";
import { z } from "zod";
import { useSuspenseAddress } from "@/addresses/hooks";
import { LabelButton } from "@/common/LabelButton";
import { SidebarMenu } from "@/common/SidebarMenu";
import { Button } from "@/components/Button";
import { Input } from "@/components/form/Input";
import { NumberInput } from "@/components/form/NumberInput";
import { useMortgage, useMortgagePatch } from "@/mortgages/hooks";
import styles from "./DialogTaxOptions.module.css";

const FormSchema = z.object({
  type: z.enum(["automatic", "override"]),
  payments: z.array(PropertyTaxPaymentSchema),
  months: z.number().nonnegative("Escrow months can't be negative"),
});

function getPaymentAmount(
  annualTax: BigNumber,
  dueDates: PropertyTaxJson[],
  index: number,
) {
  const dueDate = dueDates[index]?.dueDate;
  if (!dueDate) return null;

  const year = LocalDate.parse(dueDate).year();
  const paymentsThisYear = dueDates.filter(
    (p) => p.dueDate && LocalDate.parse(p.dueDate).year() === year,
  ).length;

  return annualTax.dividedBy(paymentsThisYear);
}

function toPropertyTaxPayments(payments: PropertyTaxJson[]) {
  const res = FormSchema.shape.payments.safeParse(payments);

  if (!res.success) {
    return null;
  }

  const valid = res.data;

  return valid.map((p) => {
    return {
      dueDate: LocalDate.parse(p.dueDate),
      isPaid: p.isPaid,
    } as PropertyTaxPayment;
  });
}

function toMortgageTax(
  options: TaxOptions<PropertyTaxPayment>[],
  form: z.infer<typeof FormSchema>,
): MortgageTax {
  if (form.type === "override") {
    return { type: "override", value: Number(form.months) };
  }

  const match = options.find((option) =>
    isEqual(option.payments.map(propertyTaxToJson), form.payments),
  );

  if (match) {
    return { type: "automatic", value: match.name };
  }

  const payments = toPropertyTaxPayments(form.payments);
  if (!payments) {
    return null;
  }

  return { type: "custom", value: payments };
}

function getDefaultValues(
  mortgage: Mortgage,
  address: Address,
): z.infer<typeof FormSchema> {
  const tax = mortgage.data.tax;
  const options = getTaxOptions(
    mortgage.data.payoff.lastPaymentDate,
    mortgage.data.payoff.firstPaymentDate,
    address.state,
  );
  const defaultPayments = (options[0]?.payments ?? []).map(propertyTaxToJson);
  const defaultOverride = 2;
  switch (tax?.type) {
    case "override":
      return { type: "override", payments: defaultPayments, months: tax.value };
    case "automatic": {
      const option = options.find((x) => x.name === tax.value);

      return {
        type: "automatic",
        months: defaultOverride,
        payments: (option?.payments ?? []).map(propertyTaxToJson),
      };
    }
    case "custom":
      return {
        type: "automatic",
        months: defaultOverride,
        payments: tax.value.map(propertyTaxToJson),
      };
    default: // Handles nulls too
      return {
        type: "automatic",
        months: defaultOverride,
        payments: defaultPayments,
      };
  }
}

interface DialogTaxOptionsProps {
  mortgageID: number;
}

export const DialogTaxOptions = (props: DialogTaxOptionsProps) => {
  const mortgage = useMortgage(props.mortgageID);
  const address = useSuspenseAddress(mortgage.addressID);
  const dialog = useDialog();
  const patch = useMortgagePatch(props.mortgageID);

  const taxOptions = useMemo(
    () =>
      getTaxOptions(
        mortgage.data.payoff.lastPaymentDate,
        mortgage.data.payoff.firstPaymentDate,
        address.state as State,
      ),
    [mortgage, address],
  );

  const { handleSubmit, formState, control, setValue, watch } = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: getDefaultValues(mortgage, address),
  });

  const formType = watch("type");

  const { fields, append, remove } = useFieldArray({
    control,
    name: "payments",
  });

  const values = watch();
  const payments = watch("payments");

  const annualTax = address?.taxes?.amount ?? new BigNumber(0);
  const tax = useMemo(
    () => toMortgageTax(taxOptions, values),
    [taxOptions, values],
  );

  const prepaidMonths = useMemo(() => {
    return getPrepaidTaxMonthsDue(
      mortgage.data.payoff.lastPaymentDate,
      mortgage.data.payoff.firstPaymentDate,
      address.state,
      tax,
    );
  }, [mortgage, address, tax]);

  const isOptionActive = useCallback(
    (option: TaxOptions<PropertyTaxPayment>) => {
      if (values.type === "automatic") {
        return isEqual(values.payments, option.payments.map(propertyTaxToJson));
      }

      return false;
    },
    [values],
  );

  const onSubmit = handleSubmit(async (form) => {
    const tax = toMortgageTax(taxOptions, form);

    if (tax?.type === "custom") {
      await patch.mutateAsync({
        tax: { type: "custom", value: tax.value.map(propertyTaxToJson) },
      });
    } else {
      await patch.mutateAsync({ tax });
    }
    dialog.close();
  });

  function selectOption(option: (typeof taxOptions)[number]) {
    setValue("type", "automatic");
    setValue("payments", option.payments.map(propertyTaxToJson));
  }

  function selectCustom() {
    setValue("type", "automatic");
  }

  function selectOverride() {
    setValue("type", "override");
  }

  return (
    <form onSubmit={onSubmit}>
      <Dialog.Header>Edit prepaid tax months</Dialog.Header>
      <Dialog.Body className={styles.body}>
        <div className={styles.sidebar}>
          <SidebarMenu>
            <SidebarMenu.Item
              active={formType === "automatic"}
              onClick={selectCustom}
            >
              Automatic
            </SidebarMenu.Item>
            <SidebarMenu.Item
              active={formType === "override"}
              onClick={selectOverride}
            >
              Manual override
            </SidebarMenu.Item>
          </SidebarMenu>
        </div>
        <div className={styles.content}>
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>How it works</h3>
            <p className={styles.explainer}>
              At closing, your lender collects prepaid property tax months into
              escrow to ensure enough funds are available when taxes come due.
              The number of months depends on your tax payment schedule and
              which payments have already been made.
            </p>
          </section>
          <hr />
          {formType === "override" ? (
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>Manual override</h3>
              <p className={styles.explainer}>
                Skip the automatic calculation and directly specify how many
                months of property tax should be collected into escrow at
                closing.
              </p>
              <NumberInput
                control={control}
                name="months"
                label="Prepaid months"
                dp={0}
              />
            </section>
          ) : (
            <>
              {taxOptions.length > 0 && (
                <>
                  <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                      Templates for {StateNames[address.state]}
                    </h3>
                    <p className={styles.explainer}>
                      Each state has common tax payment schedules. Select a
                      template to pre-fill the due dates, or customize them
                      manually.
                    </p>
                    <div className={styles.options}>
                      {taxOptions.map((option) => (
                        <LabelButton
                          key={option.name}
                          selected={isOptionActive(option)}
                          onClick={() => selectOption(option)}
                        >
                          {option.name}
                        </LabelButton>
                      ))}
                    </div>
                  </section>
                  <hr />
                </>
              )}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>Tax payment schedule</h3>
                <p className={styles.explainer}>
                  Enter the due dates for each tax installment. Mark payments
                  that have already been made before closing as paid.
                </p>
                {fields.map((field, index) => (
                  <div className={styles.row} key={field.id}>
                    <Input
                      type="date"
                      required
                      {...control.register(`payments.${index}.dueDate`)}
                    />
                    <span className={styles.amount}>
                      {formatMoney(
                        getPaymentAmount(annualTax, payments, index),
                      )}
                    </span>
                    <label className={styles.checkbox}>
                      <input
                        type="checkbox"
                        {...control.register(`payments.${index}.isPaid`)}
                      />
                      Paid
                    </label>
                    <ButtonIcon icon={TbTrash} onClick={() => remove(index)} />
                  </div>
                ))}
                <Button
                  variant="ghost"
                  icon={TbPlus}
                  onClick={() => append({ dueDate: "", isPaid: false })}
                >
                  Add payment
                </Button>
              </section>
            </>
          )}
        </div>
      </Dialog.Body>
      <Dialog.Footer className={styles.footer}>
        <span className={styles.months}>
          {formatAmount(prepaidMonths, 0)} prepaid months
        </span>
        <Button
          icon={TbDeviceFloppy}
          type="submit"
          loading={formState.isSubmitting}
        >
          Save
        </Button>
      </Dialog.Footer>
    </form>
  );
};
